import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Tooltip
} from '@mantine/core'
import {
  IconPlayerPause,
  IconPlayerPlay,
  IconRefresh,
  IconSettings
} from '@tabler/icons-react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useGetDeviceSettings } from '@/queries/deviceQueries'
import { Device, VlmAction } from '@/types/device'
import {
  getDeviceGpus,
  getVlmStatus,
  isJetsonDevice
} from '@/utils/device/getDeviceAttr'
import { DeviceTypeBadge } from '../devices/DeviceTypeBadge/DeviceTypeBadge'
import { OnlineStatus } from '../devices/OnlineStatus/OnlineStatus'
import { useFeatureUsage } from '../subscriptions/useFeatureUsage'
import { VlmActionConfirmModal } from './VlmActionConfirmModal'
import { VlmSettingsDrawer } from './VlmSettingsDrawer'
import { VlmStatusBadge } from './VlmStatusBadge'
import { toModelConfig } from './vlmModels'

type VlmDeviceCardProps = {
  device: Device
}

export const VlmDeviceCard = ({ device }: VlmDeviceCardProps) => {
  const [pendingAction, setPendingAction] = useState<VlmAction | null>(null)
  const [settingsDrawerOpened, setSettingsDrawerOpened] = useState(false)

  const isJetson = isJetsonDevice(device)
  const gpus = getDeviceGpus(device)
  const vlmStatus = getVlmStatus(device)
  const vlmServiceStatus = vlmStatus?.service_status ?? 'unknown'

  const { data: settings, isLoading: isLoadingSettings } = useGetDeviceSettings(
    device.id
  )

  const { usage: vlmAgentsUsage } = useFeatureUsage('vlm_agents')
  const hasVlmAgentsAccess = !!vlmAgentsUsage && vlmAgentsUsage.limit > 0

  const activeGpus = gpus.filter((gpu) =>
    settings?.gpu_indexes?.includes(gpu.index)
  )

  const selectedModel = settings?.model_profile

  const modelConfig = selectedModel && toModelConfig(selectedModel)

  const isVlmInProgress =
    vlmServiceStatus === 'initializing' ||
    vlmServiceStatus === 'preparing' ||
    vlmServiceStatus === 'starting_up' ||
    vlmServiceStatus === 'downloading_model'

  const isVlmActive =
    isVlmInProgress ||
    vlmServiceStatus === 'running' ||
    vlmServiceStatus === 'degraded'

  const showStart = !isVlmActive
  const showStop = isVlmActive

  const showRestart =
    vlmServiceStatus === 'running' || vlmServiceStatus === 'degraded'

  const isStartDisabled = isLoadingSettings || !settings || !hasVlmAgentsAccess
  const startTooltipId = !hasVlmAgentsAccess
    ? 'vlm.entitlement.disabledTooltip'
    : 'vlm.hardware.start.noSettingsTooltip'

  const isRestartDisabled = !hasVlmAgentsAccess

  const isSettingsCreateBlocked = !settings && !hasVlmAgentsAccess
  const isSettingsButtonDisabled = isVlmActive || isSettingsCreateBlocked
  const settingsTooltipId = isVlmActive
    ? 'vlm.hardware.settings.disabledTooltip'
    : 'vlm.entitlement.disabledTooltip'

  return (
    <Card withBorder>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Stack gap="xl">
          <Stack gap={4}>
            <Group gap="xs">
              <Text fw={600}>{device.label}</Text>
              <DeviceTypeBadge isJetson={isJetson} />
            </Group>

            <OnlineStatus status={device.active ? 'online' : 'offline'} />
          </Stack>

          {settings && device.active && (
            <>
              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  <FormattedMessage
                    id="devices.gpu.selected"
                    values={{ count: activeGpus.length }}
                  />
                </Text>
                {activeGpus.length > 0 ? (
                  <Stack gap={4} align="start" mt={2}>
                    {activeGpus.map((gpu) => (
                      <Badge
                        key={gpu.index}
                        size="sm"
                        color="gray.6"
                        variant="outline"
                      >
                        <FormattedMessage
                          id={
                            gpu.name
                              ? 'devices.gpu.indexedName'
                              : 'devices.gpu.indexLabel'
                          }
                          values={{ index: gpu.index, name: gpu.name }}
                        />
                      </Badge>
                    ))}
                  </Stack>
                ) : (
                  <Text size="sm">-</Text>
                )}
              </Stack>

              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  <FormattedMessage id="vlm.hardware.settings.model" />
                </Text>
                {modelConfig ? (
                  <Badge
                    size="sm"
                    color={modelConfig.color}
                    variant="outline"
                    mt={2}
                  >
                    {modelConfig.focus
                      ? `${modelConfig.name} / ${modelConfig.focus}`
                      : modelConfig.name}
                  </Badge>
                ) : (
                  <Text size="sm">-</Text>
                )}
              </Stack>

              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  <FormattedMessage id="vlm.hardware.settings.storageLimit" />
                </Text>
                <Text size="sm">{`${settings.disk_space} GB`}</Text>
              </Stack>

              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  <FormattedMessage id="vlm.hardware.settings.retentionDays" />
                </Text>
                <Text size="sm">
                  <FormattedMessage
                    id="vlm.hardware.settings.retentionDays.value"
                    values={{ days: settings.retention_days }}
                  />
                </Text>
              </Stack>
            </>
          )}
        </Stack>

        <Paper radius="md" p="md" bg="gray.1">
          <Text fw={600} size="sm" mb="lg">
            <FormattedMessage id="vlm.hardware.title" />
          </Text>

          <Stack gap="xl">
            <Stack gap={4}>
              {!isJetson && device.active && (
                <Stack gap={4}>
                  <Group gap="xs">
                    <VlmStatusBadge status={vlmServiceStatus} />
                    {isVlmInProgress && <Loader size={14} />}
                  </Group>

                  {isVlmInProgress && vlmStatus?.detail && (
                    <Text size="xs" c="dimmed">
                      {vlmStatus.detail}
                    </Text>
                  )}

                  {vlmServiceStatus === 'failed' && vlmStatus?.error && (
                    <Text size="xs" c="red">
                      {vlmStatus.error}
                    </Text>
                  )}
                </Stack>
              )}
            </Stack>

            {!isJetson && device.active && (
              <Group gap="xs">
                <Tooltip
                  label={<FormattedMessage id={settingsTooltipId} />}
                  disabled={!isSettingsButtonDisabled}
                >
                  <Button
                    size="xs"
                    variant="outline"
                    miw={100}
                    leftSection={<IconSettings size={16} />}
                    disabled={isSettingsButtonDisabled}
                    onClick={() => setSettingsDrawerOpened(true)}
                  >
                    <FormattedMessage id="vlm.hardware.settings" />
                  </Button>
                </Tooltip>

                {showStart && (
                  <Tooltip
                    label={<FormattedMessage id={startTooltipId} />}
                    disabled={!isStartDisabled}
                  >
                    <Button
                      size="xs"
                      color="green"
                      miw={100}
                      leftSection={<IconPlayerPlay size={16} />}
                      disabled={isStartDisabled}
                      onClick={() => setPendingAction('start')}
                    >
                      <FormattedMessage id="vlm.hardware.start" />
                    </Button>
                  </Tooltip>
                )}

                {showStop && (
                  <Button
                    size="xs"
                    variant="outline"
                    color="red"
                    miw={100}
                    leftSection={<IconPlayerPause size={16} />}
                    onClick={() => setPendingAction('stop')}
                  >
                    <FormattedMessage id="vlm.hardware.stop" />
                  </Button>
                )}

                {showRestart && (
                  <Tooltip
                    label={
                      <FormattedMessage id="vlm.entitlement.disabledTooltip" />
                    }
                    disabled={!isRestartDisabled}
                  >
                    <Button
                      size="xs"
                      variant="outline"
                      color="orange"
                      miw={100}
                      leftSection={<IconRefresh size={16} />}
                      disabled={isRestartDisabled}
                      onClick={() => setPendingAction('restart')}
                    >
                      <FormattedMessage id="vlm.hardware.restart" />
                    </Button>
                  </Tooltip>
                )}
              </Group>
            )}

            {isJetson && (
              <Text size="xs" c="yellow">
                <FormattedMessage id="vlm.hardware.jetsonWarning" />
              </Text>
            )}

            {!device.active && !isJetson && (
              <Text size="xs" c="yellow">
                <FormattedMessage id="vlm.hardware.deviceOfflineWarning" />
              </Text>
            )}
          </Stack>
        </Paper>
      </SimpleGrid>

      <VlmSettingsDrawer
        opened={settingsDrawerOpened}
        device={device}
        onClose={() => setSettingsDrawerOpened(false)}
      />

      <VlmActionConfirmModal
        action={pendingAction ?? 'stop'}
        device={device}
        opened={pendingAction !== null}
        onClose={() => setPendingAction(null)}
      />
    </Card>
  )
}
